import { useState } from "react";
import {
	CircularProgress,
	ClickAwayListener,
	Grid,
	IconButton,
	Paper,
	Popper,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LocationIcon, SearchAddressIcon, SearchCordinatesIcon } from "../../../../../../../../assets/icons";
import { CountryFlag } from "../../../../../../../../components/common/CountryFlag";
import FormSelect from "../../../../../../../../components/forms/FormSelect";
import FormTextField from "../../../../../../../../components/forms/FormTextField";
import {
	BaseNodeDTO,
	GeocodedNodeLocationPayload,
	NodeLocationDTO,
	ReverseGeocodedNodeAddressPayload,
} from "../../../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../../../datasources/networking/apiRoutes";
import { Option } from "../../../../../../../../datasources/responses/common";
import { useWriteResource } from "../../../../../../../../hooks/useResource";
import { useCurrentTheme } from "../../../../../../../../theme/theme";
import { NodeDialog } from "./NodeDialog";

interface RegistrationDetailsProps<T extends FieldValues> {
	form: UseFormReturn<T>;
	openRegistrationDetailsDialog: boolean;
	handleClose: VoidFunction;
	nodeTypes: Option[];
	countries: Option[];
	loading: boolean;
	onApplyNodeLocation: (location: NodeLocationDTO) => void;
}

export const RegistrationDetails = ({
	form,
	openRegistrationDetailsDialog,
	handleClose,
	nodeTypes,
	countries,
	loading,
	onApplyNodeLocation,
}: RegistrationDetailsProps<any>) => {
	const { t } = useTranslation("dataCollection");
	const { theme } = useCurrentTheme();

	// Geocoding result state
	const [nodeLocationResult, setNodeLocationResult] = useState<NodeLocationDTO | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	const openResult = Boolean(nodeLocationResult && anchorEl);

	// Get coordinates from address
	const { post: getLatLong, isLoading: loadingGetLatLong } = useWriteResource<
		GeocodedNodeLocationPayload,
		NodeLocationDTO
	>({
		route: ApiRoutes.ServicesGeocodeAddress,
	});

	// Get address from coordinates
	const { post: getAddress, isLoading: loadingGetAddress } = useWriteResource<
		ReverseGeocodedNodeAddressPayload,
		NodeLocationDTO
	>({
		route: ApiRoutes.ServicesReverseGeocodeLocation,
	});

	const isReadOnly = form.watch("locked");

	// Address fields validation
	const searchCoordinateAvailable =
		!!form.watch("city")?.trim() &&
		!!form.watch("address")?.trim() &&
		!!form.watch("zipCode")?.trim() &&
		!!form.watch("country")?.trim();

	const latValue = form.watch("lat");
	const lngValue = form.watch("lng");

	const hasLat = latValue !== undefined && latValue !== null && String(latValue).trim() !== "";

	const hasLng = lngValue !== undefined && lngValue !== null && String(lngValue).trim() !== "";

	const lat = Number(latValue);
	const lng = Number(lngValue);

	// Coordinates validation
	const searchAddressAvailable =
		hasLat &&
		hasLng &&
		!Number.isNaN(lat) &&
		!Number.isNaN(lng) &&
		lat >= -90 &&
		lat <= 90 &&
		lng >= -180 &&
		lng <= 180;

	// Close geocoding result
	const handleCloseLocationResult = () => {
		setNodeLocationResult(null);
		setAnchorEl(null);
	};

	// Search coordinates from address
	const handleSearchCoordinates = async (event: React.MouseEvent<HTMLButtonElement>) => {
		const button = event.currentTarget;

		const result = await getLatLong({
			city: form.getValues("city"),
			address: form.getValues("address"),
			zipCode: form.getValues("zipCode"),
			country: form.getValues("country"),
		});

		setAnchorEl(button);
		setNodeLocationResult(result);
	};

	// Search address from coordinates
	const handleSearchAddress = async (event: React.MouseEvent<HTMLButtonElement>) => {
		const button = event.currentTarget;

		const result = await getAddress({
			latitude: form.getValues("lat"),
			longitude: form.getValues("lng"),
		});

		setAnchorEl(button);
		setNodeLocationResult(result);
	};

	// Apply geocoding result
	const handleApplyLocation = () => {
		if (!nodeLocationResult) return;

		onApplyNodeLocation(nodeLocationResult);
		handleCloseLocationResult();
	};

	// Set a form value with validation
	const setFormValue = (name: string, value: unknown) => {
		form.setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	// Apply selected node from search dialog
	const handleSelectNodeDialogNodes = (node: BaseNodeDTO) => {
		setFormValue("code", node.code ?? "");
		setFormValue("typeId", node.typeId ?? null);
		setFormValue("address", node.address ?? "");
		setFormValue("zipCode", node.zipCode ?? "");
		setFormValue("city", node.city ?? "");
		setFormValue("province", node.province ?? "");
		setFormValue("country", node.country ?? "");
		setFormValue("lat", node.lat ?? null);
		setFormValue("lng", node.lng ?? null);

		handleClose();
	};

	return (
		<Grid container spacing={2}>
			<Grid size={6}>
				<FormTextField
					name="code"
					dialog={{
						title: t("detail/code GLN"),
						content: <Typography variant="body2">{t("detail/code dialog")}</Typography>,
					}}
					label={t("detail/code")}
					form={form}
					fullWidth
					rules={{
						required: t("detail/code required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={6}>
				<FormSelect
					name="typeId"
					label={t("detail/node type")}
					form={form}
					options={nodeTypes}
					rules={{
						required: t("detail/node type required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={12}>
				<FormTextField
					name="description"
					label={t("detail/location name")}
					form={form}
					fullWidth
					rules={{
						required: t("detail/location name required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={12}>
				<FormTextField
					name="address"
					label={t("detail/address")}
					form={form}
					fullWidth
					rules={{
						required: t("detail/address required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={4}>
				<FormTextField
					name="zipCode"
					label={t("detail/zip code")}
					form={form}
					fullWidth
					rules={{
						required: t("detail/zip code required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={4}>
				<FormTextField
					name="city"
					label={t("detail/city")}
					form={form}
					fullWidth
					rules={{
						required: t("detail/city required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={4}>
				<FormTextField
					name="province"
					label={t("detail/province")}
					form={form}
					fullWidth
					rules={{
						required: t("detail/province required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={12}>
				<FormSelect
					name="country"
					label={t("detail/country")}
					form={form}
					options={countries}
					rules={{
						required: t("detail/country required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={4.5}>
				<FormTextField
					name="lat"
					label={t("detail/latitude")}
					form={form}
					fullWidth
					isTypeNumber
					rules={{
						required: t("detail/latitude required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={4.5}>
				<FormTextField
					name="lng"
					label={t("detail/longitude")}
					form={form}
					fullWidth
					isTypeNumber
					rules={{
						required: t("detail/longitude required"),
					}}
					readOnly={isReadOnly}
					isLoading={loading}
				/>
			</Grid>

			<Grid size={3}>
				<Stack direction="row" spacing={0.5} justifyContent="flex-end" width="100%">
					<Tooltip
						placement="top"
						title={
							<Stack>
								<Typography variant="caption" fontWeight={700}>
									{t("detail/geographic coordinates")}
								</Typography>

								<Typography variant="caption" lineHeight={1.25}>
									{t("detail/find geographic coordinates")}
								</Typography>
							</Stack>
						}
					>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<IconButton
								onClick={handleSearchCoordinates}
								disabled={!searchCoordinateAvailable || loading || loadingGetLatLong || isReadOnly}
							>
								{loadingGetLatLong ? <CircularProgress size={18} /> : <SearchCordinatesIcon />}
							</IconButton>
						</span>
					</Tooltip>

					<Tooltip
						placement="top"
						title={
							<Stack>
								<Typography variant="caption" fontWeight={700}>
									{t("detail/address coordinates")}
								</Typography>

								<Typography variant="caption" lineHeight={1.25}>
									{t("detail/find an address")}
								</Typography>
							</Stack>
						}
					>
						<span
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<IconButton
								onClick={handleSearchAddress}
								disabled={!searchAddressAvailable || loading || loadingGetAddress || isReadOnly}
							>
								{loadingGetAddress ? <CircularProgress size={18} /> : <SearchAddressIcon />}
							</IconButton>
						</span>
					</Tooltip>
				</Stack>
			</Grid>

			<Popper
				open={openResult}
				anchorEl={anchorEl}
				placement="bottom"
				sx={{
					zIndex: (theme) => theme.zIndex.modal,
				}}
			>
				<ClickAwayListener onClickAway={handleCloseLocationResult}>
					<Paper
						elevation={4}
						onClick={handleApplyLocation}
						sx={{
							cursor: "pointer",
							borderColor: "divider",
							borderRadius: 1,
							"&:hover": {
								backgroundColor: theme.palette.background.elevation0,
							},
						}}
					>
						<Stack direction="row" spacing={2} alignItems="center" p={2}>
							<LocationIcon />

							<Typography variant="caption" display="block" whiteSpace="nowrap">
								{nodeLocationResult?.address}
								{nodeLocationResult?.zipCode && `, ${nodeLocationResult.zipCode}`}{" "}
								{nodeLocationResult?.city && `, ${nodeLocationResult.city}`}{" "}
								{nodeLocationResult?.province && `, ${nodeLocationResult.province}`}
							</Typography>

							<Stack direction="row" spacing={1} pl={1}>
								<CountryFlag countryCode={nodeLocationResult?.country} />

								<Typography variant="caption">{nodeLocationResult?.country}</Typography>
							</Stack>
						</Stack>
					</Paper>
				</ClickAwayListener>
			</Popper>

			<NodeDialog
				open={openRegistrationDetailsDialog}
				handleClose={handleClose}
				nodeTypes={nodeTypes}
				countries={countries}
				onSelectNode={handleSelectNodeDialogNodes}
			/>
		</Grid>
	);
};
